import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';

import { useAuth } from '@/components/providers/AuthProvider';
import {
  getAppLinkPathFromUrl,
  getCommunityCodeFromAppLinkUrl,
  type CommunityCode,
} from '@/constants/communities';
import { eventListTypes } from '@/constants/lists';

/**
 * App-link architecture
 *
 * This provider is the shared decision point for links to `https://app.{community-domain}/...`.
 * It is intentionally above the router stack in `app/_layout.tsx` so both external OS links and
 * in-app content links can use the same auth/community/navigation policy.
 *
 * Entry points:
 * - `app/+native-intent.ts` receives universal links on native cold/warm starts and rewrites
 *   recognized community URLs to `/deep-link?url=...`.
 * - `app/deep-link.tsx` is a transient Expo Router screen that waits for auth bootstrap, then
 *   delegates the URL here with `source: 'external'`.
 * - `components/ui/RenderHtml.tsx` calls `isAppLink()` and `openAppLink()` directly for app links
 *   embedded in posts/comments/messages, avoiding an extra `/deep-link` navigation.
 *
 * Responsibilities:
 * - Parse the target community and in-app route from a public app link.
 * - Verify stored credentials and switch community when needed through `AuthProvider`.
 * - Send unauthenticated users to the correct community sign-in screen with a redirect.
 * - Preserve existing navigation for same-community links, while seeding a parent screen for
 *   cold-start external links so detail pages have a sensible back target.
 */
type AppLinkSource = 'external' | 'internal';
type AppLinkFallback = 'none' | 'root' | 'backOrRoot';
type AppLinkNavigationAction = 'push' | 'replace';
export const EXTERNAL_APP_LINK_REDIRECT_MODE = 'externalAppLink' as const;
export type AppLinkRedirectMode = typeof EXTERNAL_APP_LINK_REDIRECT_MODE;

export const isAppLinkRedirectMode = (value: string | undefined): value is AppLinkRedirectMode => {
  return value === EXTERNAL_APP_LINK_REDIRECT_MODE;
};

interface IOpenAppLinkOptions {
  source?: AppLinkSource;
  invalidFallback?: AppLinkFallback;
  nonRoutableFallback?: AppLinkFallback;
  navigationAction?: AppLinkNavigationAction;
}

interface IAppLinkContextValue {
  isAppLink: (url: string) => boolean;
  isRoutableAppLink: (url: string) => boolean;
  isReady: boolean;
  openAppLink: (url: string, options?: IOpenAppLinkOptions) => Promise<boolean>;
  openExternalAppLinkPath: (path: string, targetCommunity: CommunityCode) => void;
}

interface IParsedAppLink {
  targetCommunity?: CommunityCode;
  targetPath?: string;
  routableDetailPath?: string;
}

interface IPendingExternalAppLinkPath {
  path: string;
  targetCommunity: CommunityCode;
}

const AppLinkContext = createContext<IAppLinkContextValue | undefined>(undefined);

const logAppLink = (...args: unknown[]) => {
  if (__DEV__) {
    console.log('[AppLink]', ...args);
  }
};

const errorAppLink = (...args: unknown[]) => {
  if (__DEV__) {
    console.error('[AppLink]', ...args);
  }
};

const getPathnameFromHref = (href: string): string => {
  return href.split(/[?#]/, 1)[0] || '/';
};

// External cold-start links may arrive with no tab stack underneath them. Seed a stable parent
// before pushing the detail so platform back/close behavior does not strand users on a leaf page.
export const getAppLinkParentPath = (href: string): string => {
  const pathname = getPathnameFromHref(href);
  const [section] = pathname.split('/').filter(Boolean);

  if (section === 'events') {
    return '/discover';
  }

  if (section === 'photo') {
    return '/feeds';
  }

  return '/feeds';
};

// Only leaf/detail routes are opened in-app. Broad list routes from public app links fall back to
// the existing screen/history because the native app already owns better list destinations.
export const getRoutableAppLinkPath = (href: string): string | undefined => {
  const pathname = getPathnameFromHref(href);
  const segments = pathname.split('/').filter(Boolean);
  const [section, detailParam] = segments;

  if (!detailParam || segments.length !== 2) {
    return undefined;
  }

  if (section === '@') {
    return href;
  }

  if (section === 'events' && !(eventListTypes as readonly string[]).includes(detailParam)) {
    return href;
  }

  if (section === 'post' && /^\d+$/.test(detailParam)) {
    return href;
  }

  if (section === 'photo') {
    return href;
  }

  // Account emails link here (e.g. `?panel=password` from the new-password email).
  if (section === 'account' && detailParam === 'settings') {
    return href;
  }

  return undefined;
};

// Keep all app-link parsing in one place so detection and navigation cannot drift apart.
const parseAppLinkUrl = (url: string): IParsedAppLink => {
  const targetCommunity = getCommunityCodeFromAppLinkUrl(url);
  const targetPath = getAppLinkPathFromUrl(url);
  const routableDetailPath = targetPath ? getRoutableAppLinkPath(targetPath) : undefined;

  return {
    targetCommunity,
    targetPath,
    routableDetailPath,
  };
};

export const isManagedAppLinkUrl = (url: string): boolean => {
  const { targetCommunity, targetPath } = parseAppLinkUrl(url);
  return !!targetCommunity && !!targetPath;
};

export const isRoutableAppLinkUrl = (url: string): boolean => {
  const { targetCommunity, routableDetailPath } = parseAppLinkUrl(url);
  return !!targetCommunity && !!routableDetailPath;
};

export function AppLinkProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { currentCommunity, getCredentials, isAuthenticated, isLoading, isAuthenticating, switchCommunity } =
    useAuth();
  const openAppLinkHandlerRef = useRef<IAppLinkContextValue['openAppLink']>(async () => false);
  const [pendingExternalAppLinkPath, setPendingExternalAppLinkPath] =
    useState<IPendingExternalAppLinkPath | null>(null);

  const navigateToFallback = useCallback(
    (fallback: AppLinkFallback) => {
      if (fallback === 'none') return;

      requestAnimationFrame(() => {
        if (fallback === 'backOrRoot' && router.canGoBack()) {
          router.back();
          return;
        }

        router.replace('/');
      });
    },
    [router]
  );

  const navigateToSignIn = useCallback(
    (
      targetCommunity: CommunityCode,
      redirect: string | undefined,
      redirectMode: AppLinkRedirectMode | undefined,
      action: AppLinkNavigationAction
    ) => {
      const signInHref = {
        pathname: '/communities/[community]/sign-in' as const,
        params: {
          community: targetCommunity,
          ...(redirect ? { redirect } : {}),
          ...(redirectMode ? { redirectMode } : {}),
        },
      };

      if (action === 'push') {
        router.push(signInHref);
        return;
      }

      router.replace(signInHref);
    },
    [router]
  );

  const navigateToPath = useCallback(
    (path: string, action: AppLinkNavigationAction) => {
      requestAnimationFrame(() => {
        if (action === 'push') {
          router.push(path as any);
          return;
        }

        router.replace(path as any);
      });
    },
    [router]
  );

  const navigateFromExternalLink = useCallback(
    (path: string) => {
      const parentPath = getAppLinkParentPath(path);

      requestAnimationFrame(() => {
        if (router.canDismiss()) {
          router.dismissAll();
        }

        router.replace(parentPath as any);
        requestAnimationFrame(() => {
          router.push(path as any);
        });
      });
    },
    [router]
  );

  const openExternalAppLinkPath = useCallback<IAppLinkContextValue['openExternalAppLinkPath']>(
    (path, targetCommunity) => {
      setPendingExternalAppLinkPath({ path, targetCommunity });
    },
    []
  );

  useEffect(() => {
    if (!pendingExternalAppLinkPath || isLoading || isAuthenticating) return;
    if (!isAuthenticated || currentCommunity !== pendingExternalAppLinkPath.targetCommunity) return;

    const { path } = pendingExternalAppLinkPath;
    setPendingExternalAppLinkPath(null);
    navigateFromExternalLink(path);
  }, [
    currentCommunity,
    isAuthenticated,
    isAuthenticating,
    isLoading,
    navigateFromExternalLink,
    pendingExternalAppLinkPath,
  ]);

  /**
   * Opens a recognized public app link inside the native app.
   *
   * `source: 'external'` means the URL came through the OS/app-link route and may have no
   * meaningful parent stack. Same-community external links preserve existing history when possible;
   * cold-start or cross-community links seed a parent route before opening the detail.
   *
   * `source: 'internal'` means a user tapped a link already rendered inside the app, so the current
   * screen should remain the parent and the detail is pushed directly.
   */
  openAppLinkHandlerRef.current = async (
    url: string,
    options?: IOpenAppLinkOptions
  ): Promise<boolean> => {
    const source = options?.source ?? 'internal';
    const invalidFallback = options?.invalidFallback ?? (source === 'external' ? 'root' : 'none');
    const nonRoutableFallback =
      options?.nonRoutableFallback ?? (source === 'external' ? 'backOrRoot' : 'none');
    const navigationAction =
      options?.navigationAction ?? (source === 'external' ? 'replace' : 'push');

    if (isLoading) {
      logAppLink('auth is still loading, skipping', url);
      return false;
    }

    const { targetCommunity, targetPath, routableDetailPath } = parseAppLinkUrl(url);

    logAppLink('parsed', {
      url,
      source,
      targetCommunity,
      targetPath,
      routableDetailPath,
    });

    if (!targetCommunity || !targetPath) {
      logAppLink('missing URL or unrecognized app link');
      navigateToFallback(invalidFallback);
      return false;
    }

    const hasCredentials = !!getCredentials(targetCommunity);
    const isAlreadyAuthenticatedInTargetCommunity =
      isAuthenticated && currentCommunity === targetCommunity;
    const shouldSwitchCommunity = targetCommunity !== currentCommunity;

    if (!hasCredentials && !isAlreadyAuthenticatedInTargetCommunity) {
      logAppLink('unauthenticated, opening sign-in', {
        community: targetCommunity,
        redirect: routableDetailPath,
      });
      navigateToSignIn(
        targetCommunity,
        routableDetailPath,
        source === 'external' ? EXTERNAL_APP_LINK_REDIRECT_MODE : undefined,
        navigationAction
      );
      return true;
    }

    if (shouldSwitchCommunity) {
      logAppLink('switching community', {
        from: currentCommunity,
        to: targetCommunity,
      });

      try {
        await switchCommunity(targetCommunity);
      } catch (error) {
        errorAppLink('Failed to switch community:', error);
        navigateToSignIn(
          targetCommunity,
          routableDetailPath,
          source === 'external' ? EXTERNAL_APP_LINK_REDIRECT_MODE : undefined,
          navigationAction
        );
        return false;
      }
    }

    if (!routableDetailPath) {
      logAppLink('path is not a detail route');
      navigateToFallback(nonRoutableFallback);
      return false;
    }

    logAppLink('navigating to detail', {
      routableDetailPath,
      shouldSwitchCommunity,
    });

    if (
      source === 'external' &&
      (!isAlreadyAuthenticatedInTargetCommunity || !router.canGoBack())
    ) {
      openExternalAppLinkPath(routableDetailPath, targetCommunity);
      return true;
    }

    navigateToPath(routableDetailPath, shouldSwitchCommunity ? 'replace' : navigationAction);
    return true;
  };

  const openAppLink = useCallback<IAppLinkContextValue['openAppLink']>((url, options) => {
    return openAppLinkHandlerRef.current(url, options);
  }, []);

  const value = useMemo<IAppLinkContextValue>(
    () => ({
      isAppLink: isManagedAppLinkUrl,
      isRoutableAppLink: isRoutableAppLinkUrl,
      isReady: !isLoading,
      openAppLink,
      openExternalAppLinkPath,
    }),
    [isLoading, openAppLink, openExternalAppLinkPath]
  );

  return <AppLinkContext.Provider value={value}>{children}</AppLinkContext.Provider>;
}

export const useAppLink = () => {
  const context = useContext(AppLinkContext);
  if (!context) {
    throw new Error('useAppLink must be used within an AppLinkProvider');
  }

  return context;
};
